import React, { useState } from 'react';

interface PracticeQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface WorkedExample {
  problem: string;
  steps: string[];
}

interface Vocabulary {
  term: string;
  definition: string;
}

interface LessonCardProps {
  lesson: {
    id: string;
    title: string;
    unit: string;
    standards: string[];
    vocabulary: Vocabulary[];
    concept: string;
    workedExample: WorkedExample;
    teacherTip: string;
    practiceQuestions: PracticeQuestion[];
  };
}

const LessonCard: React.FC<LessonCardProps> = ({ lesson }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: number }>({});
  const [showResults, setShowResults] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hoveredTerm, setHoveredTerm] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const handleSubmit = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setFilterMode('all');
  };

  const calculateScore = () => {
    const correctCount = lesson.practiceQuestions.filter(
      q => selectedAnswers[q.id] === q.correctAnswer
    ).length;
    return {
      correct: correctCount,
      total: lesson.practiceQuestions.length,
      percentage: Math.round((correctCount / lesson.practiceQuestions.length) * 100)
    };
  };

  const filteredQuestions = lesson.practiceQuestions.filter(q => {
    if (!showResults || filterMode === 'all') return true;
    const isCorrect = selectedAnswers[q.id] === q.correctAnswer;
    return filterMode === 'correct' ? isCorrect : !isCorrect;
  });

  const score = showResults ? calculateScore() : null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      {/* Header */}
      <div className="mb-6">
        <div className="text-sm text-blue-600 font-medium mb-2">{lesson.unit}</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{lesson.title}</h2>
        <div className="flex flex-wrap gap-2">
          {lesson.standards.map((standard, idx) => (
            <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
              {standard}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'overview'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className="ri-book-open-line mr-2"></i>
          Lesson Overview
        </button>
        <button
          onClick={() => setActiveTab('practice')}
          className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'practice'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className="ri-question-answer-line mr-2"></i>
          Practice ({lesson.practiceQuestions.length} Questions)
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Vocabulary */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <i className="ri-book-2-line mr-2 text-blue-600"></i>
              Key Vocabulary
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {lesson.vocabulary.map((item, idx) => (
                <div
                  key={idx}
                  className="relative p-3 bg-gray-50 rounded-lg cursor-help"
                  onMouseEnter={() => setHoveredTerm(item.term)}
                  onMouseLeave={() => setHoveredTerm(null)}
                >
                  <span className="font-semibold text-gray-900">{item.term}</span>
                  {hoveredTerm === item.term && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-2 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg">
                      {item.definition}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Core Concept */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line mr-2 text-yellow-600"></i>
              Core Concept
            </h3>
            <p className="text-gray-700 leading-relaxed bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              {lesson.concept}
            </p>
          </div>

          {/* Worked Example */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <i className="ri-pencil-ruler-2-line mr-2 text-green-600"></i>
              Worked Example
            </h3>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900 mb-3">{lesson.workedExample.problem}</p>
              <button
                onClick={() => {
                  setShowExample(!showExample);
                  setCurrentStep(0);
                }}
                className="mb-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                {showExample ? 'Hide Solution' : 'Show Step-by-Step Solution'}
              </button>
              {showExample && (
                <div className="space-y-3">
                  {lesson.workedExample.steps.slice(0, currentStep + 1).map((step, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                      <span className="font-semibold text-green-700 mr-2">Step {idx + 1}:</span>
                      <span className="text-gray-800">{step}</span>
                    </div>
                  ))}
                  {currentStep < lesson.workedExample.steps.length - 1 && (
                    <button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      Next Step
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Teacher Tip */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <i className="ri-user-star-line mr-2 text-purple-600"></i>
              Mr. Augustine&apos;s AP Tip
            </h3>
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <p className="text-gray-700 leading-relaxed">{lesson.teacherTip}</p>
            </div>
          </div>
        </div>
      )}

      {/* Practice Tab */}
      {activeTab === 'practice' && (
        <div>
          {/* Score Display */}
          {showResults && score && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-600">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Your Score</h4>
                  <p className="text-3xl font-bold text-blue-600">
                    {score.correct}/{score.total} ({score.percentage}%)
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                      filterMode === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterMode('correct')}
                    className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                      filterMode === 'correct'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Correct
                  </button>
                  <button
                    onClick={() => setFilterMode('incorrect')}
                    className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                      filterMode === 'incorrect'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Incorrect
                  </button>
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {!showResults && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>
                  Progress: {Object.keys(selectedAnswers).length}/{lesson.practiceQuestions.length}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(Object.keys(selectedAnswers).length / lesson.practiceQuestions.length) * 100}%`
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Questions */}
          <div className="space-y-6">
            {filteredQuestions.map((question, qIdx) => {
              const originalIdx = lesson.practiceQuestions.findIndex(q => q.id === question.id);
              const isAnswered = question.id in selectedAnswers;
              const selectedAnswer = selectedAnswers[question.id];
              const isCorrect = selectedAnswer === question.correctAnswer;

              return (
                <div
                  key={question.id}
                  className={`p-4 rounded-lg border-2 ${
                    showResults
                      ? isCorrect
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <p className="font-semibold text-gray-900 mb-3">
                    {originalIdx + 1}. {question.question}
                  </p>
                  <div className="space-y-2">
                    {question.options.map((option, optIdx) => {
                      const isSelected = selectedAnswer === optIdx;
                      const isCorrectOption = optIdx === question.correctAnswer;

                      return (
                        <button
                          key={optIdx}
                          onClick={() => !showResults && handleAnswerSelect(question.id, optIdx)}
                          disabled={showResults}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                            showResults
                              ? isCorrectOption
                                ? 'border-green-500 bg-green-100'
                                : isSelected
                                ? 'border-red-500 bg-red-100'
                                : 'border-gray-300 bg-white'
                              : isSelected
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 bg-white hover:border-blue-400'
                          }`}
                        >
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + optIdx)}.
                          </span>
                          {option}
                          {showResults && isCorrectOption && (
                            <i className="ri-check-line ml-2 text-green-600"></i>
                          )}
                          {showResults && isSelected && !isCorrectOption && (
                            <i className="ri-close-line ml-2 text-red-600"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {showResults && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                      <p className="text-sm text-gray-700">
                        <strong>Explanation:</strong> {question.explanation}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Submit Button */}
          {!showResults && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={Object.keys(selectedAnswers).length === 0}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg font-semibold whitespace-nowrap"
              >
                Submit Answers
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonCard;